import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, ShieldAlert, Trash2, Info } from "lucide-react";

const ConfirmCtx = createContext(null);

/**
 * Provider that renders one AlertDialog and exposes an imperative `confirm(opts)`
 * returning a Promise<boolean>. Drop-in replacement for window.confirm().
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, opts: {} });
  const resolveRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, opts });
    });
  }, []);

  const close = (val) => {
    setState((s) => ({ ...s, open: false }));
    if (resolveRef.current) { resolveRef.current(val); resolveRef.current = null; }
  };

  const o = state.opts || {};
  const tone = o.tone || "danger"; // danger, warning, info
  const Icon = tone === "danger" ? Trash2 : tone === "warning" ? AlertTriangle : Info;
  const iconWrap = {
    danger: "bg-red-50 text-[#DC2626]",
    warning: "bg-amber-50 text-amber-700",
    info: "bg-blue-50 text-blue-700",
  }[tone];
  const actionCls = {
    danger: "bg-[#DC2626] hover:bg-[#B91C1C] focus:ring-red-500",
    warning: "bg-amber-500 hover:bg-amber-600 focus:ring-amber-400",
    info: "bg-[#DC2626] hover:bg-[#B91C1C]",
  }[tone];

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(open) => !open && close(false)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <div className="flex gap-4">
              <div className={`h-11 w-11 shrink-0 rounded-full grid place-items-center ${iconWrap}`}>
                <Icon className="h-5 w-5" strokeWidth={1.8} />
              </div>
              <div className="min-w-0 flex-1">
                <AlertDialogTitle className="text-left text-slate-900">{o.title || "Are you sure?"}</AlertDialogTitle>
                {o.description && (
                  <AlertDialogDescription className="text-left text-slate-500 mt-1.5 whitespace-pre-wrap">
                    {o.description}
                  </AlertDialogDescription>
                )}
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">{o.cancelLabel || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction className={actionCls} onClick={() => close(true)} data-testid="confirm-ok">
              {o.confirmLabel || "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}
